
import CountryRepository from '@/repositories/CountryRepository';
import AddressTypeRepository from '@/repositories/AddressTypeRepository';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import ContactEmailPhoneForm from '@/components/global/ContactEmailPhoneForm.vue';
import ContactPersonBasicInfo from '@/components/global/client/ContactPersonBasicInfo.vue';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { PopupEventData } from '@/interfaces/components/PopupEventData';
import { PopupEvents } from '@/enums/global/PopupEvents';
import { ContactDelete } from '@/interfaces/ContactDelete';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';
import ContactPerson from '@/models/ContactPerson';
import Project from '@/models/Project';
import ContactConfig from '@/models/interfaces/Contact';
import ClientRepository from '@/repositories/ClientRepository';
import ContactPersonRepository from '@/repositories/ContactPersonRepository';
import { getDatepickerLocale } from '@/helpers/LocaleHelper';
import ClientAddressForm from '@/components/views/ClientView/ClientAddressForm.vue';

@Component({
    name: 'ContactInformation',
    components: { ContactEmailPhoneForm, ContactPersonBasicInfo, ClientAddressForm },
})
export default class ContactInformation extends Vue {
    @Prop({ required: true }) private value!: any;
    @Prop({ required: false }) private defaultCountryId!: string;
    @Prop({ default: false }) private emailValidationOverride!: boolean;
    @Prop({ default: false }) private emailValidationOverrideValue!: boolean;
    @Prop({ default: null }) private clientId!: string;
    @Prop({ default: () => [] }) private contactErrors!: [];
    @Prop({ default: false }) private isNewClient!: boolean;
    @Prop({ required: true }) private canAccessLogistics!: boolean;

    private contacts = [...this.value.contacts];
    private loadingOverlay = new LoadingOverlayHelper(this, {});
    private get defaultAddressStringSufix() {
        return this.$t(' (default)');
    }

    private contactEmailAndPhoneForm = {
        emails: [''],
        phones: [''],
        primaryIndex: 0,
    };

    private emptyContact() {
        return {
            name: '',
            email: '',
            phone: '',
            job: '',
            address: { ...this.defaultClientAddress },
            birthday: '',
            isDefault: !this.value.contacts.length ? true : false,
            useDefaultAddress: true,
        };
    }

    private get locale() {
        return getDatepickerLocale();
    }

    private emptyAddress() {
        return {
            streetNameAndNumber: '',
            poNumber: '',
            city: '',
            building: '',
            country: this.formattedDefaultCountry,
            b2bDeliveryPhone: '',
            b2bDeliveryEmail: '',
            b2bDeliveryNotes: '',
            b2bDeliveryDays: [],
        };
    }

    private onAddContact() {
        this.contacts.push(this.emptyContact());
    }

    private get flagUrl() {
        // return a function for getter
        return (countryCode: string | undefined) =>
            countryCode ? `url(https://flagsapi.com/${countryCode}/flat/48.png)` : '';
    }

    private get countries() {
        return CountryRepository.getAll();
    }

    private get defaultCountry() {
        return CountryRepository.getById(this.defaultCountryId);
    }

    private get formattedDefaultCountry() {
        return this.defaultCountry ? this.defaultCountry.id + '|' + this.defaultCountry.code : undefined;
    }

    private get isCompany() {
        return this.value.isCompany;
    }

    private get defaultClientAddress() {
        const selectedAddress =
            this.value.clientAddresses?.find((address: any) => address.isDefault) || this.emptyAddress();

        return { ...selectedAddress, isDefault: false, addressType: selectedAddress?.addressType?.id || '' };
    }

    private get defaultAddressType() {
        return this.value.clientAddresses?.find((address: any) => address.isDefault)?.addressType;
    }

    private getAddressTypeName(addressTypeId: string | undefined): string {
        if (!addressTypeId) return '';
        const addressType = AddressTypeRepository.getById(addressTypeId);
        return addressType?.name || addressTypeId;
    }

    private get clientAddressOptions() {
        if (!this.value.clientAddresses) return [];

        const options = this.value.clientAddresses
            .filter((address: any) => address?.addressType)
            .map((address: any) => {
                const addressTypeName = this.getAddressTypeName(address.addressType);
                return {
                    addressType: address.addressType,
                    label: address.isDefault ? addressTypeName + this.defaultAddressStringSufix : addressTypeName,
                };
            });

        if (!options.length) {
            return [{ addressType: '', label: this.$t('Default client address') }];
        }

        return options;
    }

    private getMatchingAddressType(contact: ContactConfig) {
        const contactAddr = contact.address;
        if (!contactAddr) return undefined;

        const matchingAddress = this.value.clientAddresses?.find((addr: any) => {
            if (contactAddr.id && addr.id) return addr.id === contactAddr.id;
            // Local id for unsaved addresses
            if (contactAddr._localId && addr._localId) return addr._localId === contactAddr._localId;
            return false;
        });

        return matchingAddress?.addressType ?? this.defaultAddressType ?? '';
    }

    private onDefaultContactPersonChange(i: number, checked: boolean) {
        if (checked) {
            this.contacts[i].isDefault = checked;

            this.contacts.forEach((contact: ContactConfig, index: number) => {
                if (i !== index && contact.isDefault) {
                    contact.isDefault = false;
                }
            });
        } else {
            const message = this.isCompany
                ? 'You need to have one default contact person!'
                : 'You need to have one default family member!';

            this.$notification.warning({
                message: this.$t('Warning') as string,
                description: this.$t(message),
                duration: 2,
            });
        }
    }

    private onUseDefaultAddressChange(index: number, checked: boolean) {
        const contact = this.contacts[index];

        contact.useDefaultAddress = checked;

        if (checked) {
            if (this.value.clientAddresses?.length) {
                contact.address = { ...this.defaultClientAddress };
            }

            return;
        }

        const originalAddressId = contact.address_id;

        if (!originalAddressId) {
            this.$set(contact, 'address', {
                ...contact.address,
                id: null,
            });

            return;
        }

        // We use address_id as a cached value so the original address
        // can be restored after toggling "use default address".
        const addressExists = this.clientAddressesContainContactAddress(originalAddressId);

        this.$set(contact, 'address', {
            ...contact.address,
            id: addressExists ? null : originalAddressId,
        });
    }

    private clientAddressesContainContactAddress(contactAddressId: string) {
        if (!this.value.clientAddresses.length || !contactAddressId) {
            return false;
        }

        return this.value.clientAddresses?.some((address: any) => address.id === contactAddressId);
    }

    private onChangeSelectedClientAddress(i: number, addressType: string) {
        const selectedAddress = this.value.clientAddresses?.find((address: any) => address.addressType === addressType);

        if (selectedAddress && this.contacts[i]) {
            this.contacts[i].address = { ...selectedAddress };
        }
    }

    private async updateContactWithProjects(contactId: string) {
        this.loadingOverlay.start();
        try {
            await ContactPerson.getByIdWithProjects(contactId);
        } catch {
            this.$notification.error({
                message: this.$t('Dogodila se greška') as string,
                description: this.$t('Greška prilikom učitavanja podataka o kontakt osobi.'),
            });
        } finally {
            this.loadingOverlay.stop();
        }
    }

    private async deleteContact({ pendingContactIndex, contact }: ContactDelete) {
        if (this.contacts.length === 1) {
            this.$notification.warning({
                message: this.$t('At least one contact person is required.') as string,
                description: '',
            });

            return;
        }

        await this.updateContactWithProjects(contact.id);
        await this.$nextTick();

        let filteredContacts = this.value.contacts.filter((pendingContact: any) => pendingContact.name !== '');
        contact = ContactPersonRepository.getById(contact.id) || contact;
        filteredContacts = filteredContacts.map((item: ContactConfig) => ContactPersonRepository.getById(item.id));

        const popupData: PopupEventData = {
            popupEvent: PopupEvents.deleteContactPerson,
            data: {
                pendingContactIndex,
                contact,
                clientId: this.value.id,
                deleteContact: this.deleteContactAndClosePopup.bind(this),
                contactPersons: filteredContacts,
            },
        };

        EventBus.$emit(EventBusEvents.onDeleteContactPerson, popupData);
    }

    private async deleteContactAndClosePopup(contactDeleteSettings: ContactDelete) {
        try {
            await this.deleteContactRemoteAndLocal(contactDeleteSettings);
        } catch (error) {
            this.loadingOverlay.stop();
            this.$notification.error({
                message: this.$t('Dogodila se greška') as string,
                description: (error as Error).message,
            });
        }
        EventBus.$emit(EventBusEvents.closePopup);

        const message = this.isCompany ? 'Kontakt osoba uspješno obrisana!' : 'Family member sucessfully deleted!';

        this.$notification.success({
            message: this.$t(message) as string,
            description: '',
        });
    }

    private async deleteContactRemoteAndLocal({ pendingContactIndex, contact, newContactPersonId }: ContactDelete) {
        this.removeContactFromArray(pendingContactIndex);

        this.loadingOverlay.start();
        if (contact.id == null || newContactPersonId == null) {
            this.loadingOverlay.stop();
            return;
        }

        try {
            for await (const project of contact.projects) {
                await Project.updateExisting({
                    projectId: project.projectId,
                    contactPersonId: newContactPersonId,
                });
            }
        } catch (error) {
            return Promise.reject(error);
        }

        if (contact.id != null) {
            await ContactPerson.deleteExisting(contact.id);
        }

        this.loadingOverlay.stop();
        return Promise.resolve();
    }

    private removeContactFromArray(pendingContactKey: number) {
        this.contacts.splice(pendingContactKey, 1);
    }

    private formatCountryValue(id: string) {
        const country = this.countries.find((countryVal) => countryVal.id === id);
        if (country) {
            return country.id + '|' + country.code;
        }
    }

    private formatContactsAddress() {
        if (this.contacts) {
            this.contacts.forEach((contact: ContactConfig) => {
                if (!contact.address || !contact.address.country) {
                    return;
                }

                if (typeof contact.address.country !== 'string') {
                    contact.address.country = this.formatCountryValue(contact.address.country.id);
                    return;
                }

                if (!contact.address.country.includes('|')) {
                    contact.address.country = this.formatCountryValue(contact.address.country);
                }
            });
        }
    }

    private updateAddress(updatedAddress: any, index: number) {
        this.$set(this.contacts[index], 'address', updatedAddress);
    }

    private async created() {
        // Contact initialization logic has been moved to parent components
        // (NewClientModal and NewEditClientView) to ensure it runs when the client
        // is loaded, not when this tab is clicked/mounted.
        this.$emit('contacts-initialized');
    }

    @Watch('contacts', {
        immediate: true,
        deep: true,
    })
    private onContactsChange() {
        this.formatContactsAddress();
        this.value.contacts = this.contacts;
        this.$emit('input', this.value);
    }
}
